import React, { useState } from "react";
import { createPortal } from "react-dom";
import { updateAuthorStatus } from "../../../api";
import { ReactComponent as Close } from 'src/assets/close.svg';

interface RejectReasonModalProps {
    authorName: string;
    authorEmail: string;
    onClose: () => void;
    onSuccess: () => void;
}

const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
    authorName,
    authorEmail,
    onClose,
    onSuccess
}) => {
    const [step, setStep] = useState<'WRITE' | 'SUCCESS'>('WRITE');
    const [reason, setReason] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const validateReason = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) {
            return "반려 사유를 입력해 주세요.";
        }
        const withoutTags = trimmed.replace(/<[^>]*>/g, '').trim();
        if (!withoutTags) {
            return "HTML 태그만 있는 값은 반려 사유로 입력할 수 없습니다.";
        }
        if (trimmed.length > 1000) {
            return "반려 사유는 1000자를 초과할 수 없습니다.";
        }
        return null;
    };

    const handleRejectSubmit = async () => {
        const validationError = validateReason(reason);
        if (validationError) {
            setErrorMsg(validationError);
            return;
        }
        setErrorMsg('');

        const confirmReject = window.confirm("반려하시겠습니까?");
        if (!confirmReject) return;

        setIsLoading(true);
        try {
            await updateAuthorStatus(authorEmail, 'SUSPENDED', reason);
            setStep('SUCCESS');
        } catch (error: any) {
            console.error("Error updating author status to SUSPENDED:", error);
            const serverMsg = error.response?.data?.message || "반려 처리에 실패했습니다.";
            setErrorMsg(serverMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return createPortal(
        <>
            {/* 오버레이 */}
            <div className="overlay" onClick={step === 'WRITE' ? onClose : undefined} />

            {step === 'WRITE' ? (
                <div className="penpal-alert-goods bg-white rounded-[20px] outline-none z-[10000] relative">
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors z-20"
                        style={{ border: 'none', background: 'transparent' }}
                    >
                        <Close className="w-4 h-4 text-gray-400" />
                    </button>

                    <div className="flex flex-col p-8 pt-10 gap-6">
                        {/* Header */}
                        <div className="text-center">
                            <h2 className="text-[18px] font-bold text-[#222]">반려 사유 작성</h2>
                        </div>

                        {/* Description */}
                        <div className="text-center text-[#555] text-[14px] leading-relaxed">
                            <p className="mb-1">
                                <span className="font-bold text-[#222]">{authorName}</span> 님의 작가 신청을 반려합니다.
                            </p>
                            <p className="text-gray-400 text-[12px]">
                                반려 사유를 작성해 주세요. 해당 내용은 신청자에게 이메일로 전달됩니다.
                            </p>
                        </div>

                        {/* Input Area */}
                        <div className="flex flex-col gap-2">
                            <textarea
                                value={reason}
                                onChange={(e) => {
                                    setReason(e.target.value);
                                    if (errorMsg) setErrorMsg('');
                                }}
                                placeholder="예) 제출하신 작품 소개 자료가 기준에 부합하지 않아 반려 처리되었습니다. 추후 재신청 시 작품 소개를 보완하여 제출해 주시면 검토하겠습니다."
                                className="w-full p-4 border border-[#e0e0e0] rounded-xl text-[13px] text-[#333] leading-relaxed focus:outline-none focus:border-[#19cf7a] transition-colors"
                                rows={6}
                                maxLength={1100}
                                style={{ resize: 'none' }}
                                disabled={isLoading}
                            />
                            <div className="flex justify-between items-center px-1">
                                {errorMsg ? (
                                    <span className="text-red-500 text-[11px] font-semibold">{errorMsg}</span>
                                ) : (
                                    <span className="text-gray-400 text-[11px]">✏️ 작성 내용은 메일 발송 전 수정 가능합니다</span>
                                )}
                                <span className={`text-[11px] ${reason.length > 1000 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                                    {reason.length} / 1000자
                                </span>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex gap-2.5 justify-center">
                            <button
                                onClick={onClose}
                                className="flex-1 h-11 border border-[#e0e0e0] text-[#666] font-semibold rounded-xl hover:bg-gray-50 transition-colors text-[14px]"
                                style={{ background: 'transparent' }}
                                disabled={isLoading}
                            >
                                취소
                            </button>
                            <button
                                onClick={handleRejectSubmit}
                                className="flex-1 h-11 bg-red-500 hover:bg-red-600 transition-colors text-white font-bold rounded-xl shadow-sm text-[14px]"
                                style={{ border: 'none' }}
                                disabled={isLoading}
                            >
                                {isLoading ? '처리 중...' : '반려 처리'}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="penpal-alert bg-white rounded-[20px] outline-none z-[10000]">
                    <div className="flex flex-col p-8 pt-10 gap-6 text-center">
                        {/* Header */}
                        <div>
                            <h2 className="text-[18px] font-bold text-[#222]">반려 처리가 완료되었습니다</h2>
                        </div>

                        {/* Body */}
                        <div className="text-[#555] text-[14px] leading-relaxed">
                            <span className="font-bold text-[#222]">{authorName}</span> 님께 반려 사유가 포함된<br />이메일이 발송되었습니다.
                        </div>

                        {/* Button */}
                        <div className="flex justify-center">
                            <button
                                onClick={onSuccess}
                                className="w-full h-11 bg-[#19cf7a] hover:bg-[#00b368] transition-colors text-white font-bold rounded-xl shadow-sm text-[14px]"
                                style={{ border: 'none' }}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default RejectReasonModal;
